import { useEffect } from "react";
import styled from "styled-components";
import useParticipants from "hooks/livekit/useParticipants/useParticipants";
import { useVolumeContext } from "hooks/useVolumeContext/useVolumeContext";

const VolumeIcon = ({ volume }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5L6 9H3V15H6L11 19V5Z" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {volume > 0.5 && (
      <>
        <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.07 5.93C19.9447 7.80528 20.9979 10.3462 20.9979 13C20.9979 15.6538 19.9447 18.1947 18.07 20.07" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    )}
    {volume > 0 && volume <= 0.5 && (
      <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    )}
    {volume === 0 && (
      <path d="M16 9L20 13M20 9L16 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

export const VolumeControl = ({ onVolumeChange }) => {
  const { volume, setVolume } = useVolumeContext();
  const participants = useParticipants();

  useEffect(() => {
    if (participants.length > 0) {
      applyVolumeToTracks(volume);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participants, volume]);

  const applyVolumeToTracks = (newVolume) => {
    participants
      .filter((participant) => participant.identity.includes("observer"))
      .forEach((participant) => {
        if (participant.audioTrackPublications) {
          participant.audioTrackPublications.forEach((publication) => {
            const audioTrack = publication.track;
            if (audioTrack && typeof audioTrack.setVolume === 'function') {
              audioTrack.setVolume(newVolume);
            }
          });
        }
      });
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);

    applyVolumeToTracks(newVolume);

    onVolumeChange?.(newVolume);
  };

  return (
    <VolumeContainer>
      <SliderContainer>
        <Slider
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolumeChange}
          $volume={volume}
        />
      </SliderContainer>
      <VolumeButton $volume={volume} aria-label={volume === 0 ? "Volume (muted)" : "Volume"}>
        <VolumeIcon volume={volume} />
      </VolumeButton>
    </VolumeContainer>
  );
};

const VolumeContainer = styled.div`
  position: absolute;
  left: 4px;
  bottom: 8px;
  z-index: 100;
`;

const SliderContainer = styled.div`
  position: absolute;
  bottom: calc(100% + 20px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: slideUp 0.2s ease-out;
  pointer-events: auto;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const Slider = styled.input`
  -webkit-appearance: none;
  width: 120px;
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(
    to right,
    #5B6FED 0%,
    #5B6FED ${props => props.$volume * 100}%,
    #E5E7EB ${props => props.$volume * 100}%,
    #E5E7EB 100%
  );
  outline: none;
  transform: rotate(-90deg);
  margin: 50px 0;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #5B6FED;
    cursor: pointer;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.3);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #5B6FED;
    cursor: pointer;
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
  }

  &::-moz-range-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.3);
  }
`;

const VolumeButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.$volume === 0 ? '#8E8E93' : '#E8684A'};
  border: 3px solid white;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
  }
`;
